import React, { useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Btn, Loading, Msg, Section, TextInput, useAdminGet } from '../kit';
import { S, str } from './shared';

/**
 * Recrawl → Guide: recrawl/GUIDE.md (how the engine works, how to add a
 * university, troubleshooting) as served by GET guide. When the server falls
 * back to DESIGN.md (guide missing) only a short notice is shown: the design
 * document is not panel copy. Rendered with react-markdown +
 * remark-gfm, which the chat already ships (the guide leans on tables), so
 * nothing new is installed. A contents list from the headings, and a filter
 * that keeps the sections whose text matches.
 */

const slug = (s: string) => s.toLowerCase().replace(/[`*_~]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const textOf = (children: React.ReactNode): string => {
  if (children === null || children === undefined || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(textOf).join('');
  if (React.isValidElement(children)) return textOf((children.props as { children?: React.ReactNode }).children);
  return '';
};

/** The markdown split at its ## / ### headings, so the filter can keep whole sections. */
const sections = (md: string) => {
  const out: { level: number; title: string; body: string }[] = [];
  let cur = { level: 0, title: '', body: '' };
  let fence = false;
  md.split('\n').forEach(line => {
    if (/^```/.test(line)) fence = !fence;
    const m = !fence ? /^(#{1,3})\s+(.*)$/.exec(line) : null;
    if (m) {
      if (cur.body.trim() || cur.title) out.push(cur);
      cur = { level: m[1].length, title: m[2].trim(), body: `${line}\n` };
    } else cur.body += `${line}\n`;
  });
  if (cur.body.trim() || cur.title) out.push(cur);
  return out;
};

const H: React.FC<{ level: number; children: React.ReactNode }> = ({ level, children }) => {
  const id = slug(textOf(children));
  const cls = level === 1 ? 'text-xl font-bold mt-2' : level === 2 ? 'text-lg font-bold mt-4 pt-2 border-t border-grayscale-200'
    : level === 3 ? 'text-base font-bold mt-3' : 'text-sm font-bold mt-2';
  const Tag = (`h${Math.min(6, level)}`) as 'h1';
  return <Tag id={`guide-${id}`} className={`${cls} text-grayscale-1300 font-grotesk scroll-mt-4`}>{children}</Tag>;
};

const components: any = {
  h1: ({ children }: any) => <H level={1}>{children}</H>,
  h2: ({ children }: any) => <H level={2}>{children}</H>,
  h3: ({ children }: any) => <H level={3}>{children}</H>,
  h4: ({ children }: any) => <H level={4}>{children}</H>,
  p: ({ children }: any) => <p className="text-[13.5px] leading-relaxed text-grayscale-1200 my-2">{children}</p>,
  ul: ({ children }: any) => <ul className="list-disc pl-6 my-2 text-[13.5px] text-grayscale-1200 flex flex-col gap-1">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal pl-6 my-2 text-[13.5px] text-grayscale-1200 flex flex-col gap-1">{children}</ol>,
  li: ({ children }: any) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }: any) => <a href={href} target="_blank" rel="noreferrer noopener" className="text-primary-700 underline underline-offset-2">{children}</a>,
  blockquote: ({ children }: any) => <blockquote className="border-l-4 border-primary-300 bg-primary-100/40 px-3 py-1 my-2 text-grayscale-1200">{children}</blockquote>,
  hr: () => <hr className="my-4 border-grayscale-200" />,
  table: ({ children }: any) => <div className="overflow-auto my-3 rounded-lg border border-grayscale-300"><table className="w-full text-[12.5px]">{children}</table></div>,
  thead: ({ children }: any) => <thead className="bg-grayscale-200 text-grayscale-1200">{children}</thead>,
  th: ({ children }: any) => <th className="text-left px-2.5 py-1.5 font-bold align-top">{children}</th>,
  td: ({ children }: any) => <td className="px-2.5 py-1.5 border-t border-grayscale-200 align-top text-grayscale-1200">{children}</td>,
  pre: ({ children }: any) => <pre className="my-2 rounded-lg bg-grayscale-1300 text-grayscale-200 text-[12px] leading-snug p-3 overflow-auto">{children}</pre>,
  code: ({ className, children }: any) => (className
    ? <code className={`${className} font-mono`}>{children}</code>
    : <code className="font-mono text-[12px] bg-grayscale-200 text-grayscale-1300 rounded px-1 py-px [overflow-wrap:anywhere]">{children}</code>),
};

const Guide: React.FC = () => {
  const res = useAdminGet<any>(S, 'guide', {}, []);
  // An older server sends the technical design when the guide file is absent: not shown in the panel.
  const missing = !!res.data?.missing;
  const text = missing ? '' : str(res.data?.text);
  const [q, setQ] = useState('');
  const top = useRef<HTMLDivElement | null>(null);
  const parts = useMemo(() => sections(text), [text]);
  const toc = parts.filter(p => p.level >= 1 && p.level <= 3 && p.title);
  const needle = q.trim().toLowerCase();
  const shown = needle ? parts.filter(p => p.body.toLowerCase().includes(needle)) : parts;
  const md = shown.map(p => p.body).join('\n');

  if (res.loading && !res.data) return <Loading label="Loading the guide…" />;
  if (res.error && !res.data) return <Msg kind="error">{res.error}</Msg>;
  return (
    <div ref={top} className="grid gap-4 items-start lg:grid-cols-[16rem_minmax(0,1fr)]">
      <div className="bg-white rounded-xl border border-grayscale-300 p-3 flex flex-col gap-1 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] overflow-auto">
        <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">Contents</div>
        {toc.map((t, i) => (
          <a key={i} href={`#guide-${slug(t.title)}`}
            onClick={e => { e.preventDefault(); document.getElementById(`guide-${slug(t.title)}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            className={`text-[12.5px] hover:text-primary-700 truncate ${t.level === 1 ? 'font-bold text-grayscale-1300' : t.level === 2 ? 'font-medium text-grayscale-1200 mt-1' : 'pl-3 text-grayscale-1100'} ${needle && !shown.includes(t) ? 'opacity-40' : ''}`}
            title={t.title}>{t.title.replace(/`/g, '')}</a>
        ))}
        {!toc.length && <span className="text-xs text-grayscale-1100">No headings.</span>}
      </div>
      <Section title="Recrawl guide"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <TextInput value={q} onChange={setQ} placeholder="Filter sections…" />
            {q && <Btn small onClick={() => setQ('')}>Clear</Btn>}
          </div>
        }>
        {missing && <Msg kind="error">The guide is not available on this server.</Msg>}
        {needle && <div className="text-xs text-grayscale-1100">{shown.length} of {parts.length} sections mention “{q.trim()}”.</div>}
        {!text ? (!missing && <div className="text-sm text-grayscale-1100">The guide is empty.</div>) : (
          <div className="min-w-0 [overflow-wrap:anywhere]">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{md}</ReactMarkdown>
          </div>
        )}
      </Section>
    </div>
  );
};

export default Guide;
